import {Button, CardActions, TextField} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import React, {useEffect, useRef, useState} from 'react'
import {sleep} from '../../Helpers/utils'
import {BOT_NAME, GENERAL_REDUCER, VIDEO_REDUCER} from '../../redux/consts'
import {
  CAPTURED_IMAGE,
  IS_PHOTO_CAPTURE_OPEN,
  PHOTO_CAPTURE_WIZARD_STEP,
  WHO_IS_IN_THE_PHOTO
} from '../../redux/video/consts'
import styles from './CapturePhotoDialog.module.scss'
import {useDispatch, useSelector} from 'react-redux'
import {savePhoto, setIsPhotoCaptureOpen, setPhotoCaptureWizardStep, setWhoIsInThePhoto} from '../../redux/actions'
import BasicModal from '../Generic/BasicModal'
import useCapturePhoto from './useCapturePhoto'; // Import the custom hook
const TIME_TO_ALLOW_PHOTO_CAPTURE = 1000*60

export const CapturePhotoDialog = () => {
  const isPhotoCaptureOpen = useSelector(state => state.getIn([VIDEO_REDUCER,IS_PHOTO_CAPTURE_OPEN]))
  const capturedImage = useSelector(state => state.getIn([VIDEO_REDUCER,CAPTURED_IMAGE]))
  const photoCaptureWizardStep = useSelector(state => state.getIn([VIDEO_REDUCER,PHOTO_CAPTURE_WIZARD_STEP]))
  const dispatch = useDispatch()
  const botName = useSelector(state => state.getIn([GENERAL_REDUCER,BOT_NAME]))
  const [capturePhoto] = useCapturePhoto()
  const whoIsInThePhoto = useSelector(state => state.getIn([VIDEO_REDUCER,WHO_IS_IN_THE_PHOTO]))
  const [isDialogExpanded, setIsDialogExpanded] = useState(false)
  const setIsOpen = (isOpen) => dispatch(setIsPhotoCaptureOpen(isOpen))
  const timeoutRef = useRef(null) // <-- New ref to store timeout ID
  const resetAutoClose = React.useCallback(() => {
    if (!isPhotoCaptureOpen) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      dispatch(setIsPhotoCaptureOpen(false))
      timeoutRef.current = null
    }, TIME_TO_ALLOW_PHOTO_CAPTURE)
  }, [dispatch, isPhotoCaptureOpen])

  useEffect(() => {
    if (isPhotoCaptureOpen && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        dispatch(setIsPhotoCaptureOpen(false))
        timeoutRef.current = null
      }, TIME_TO_ALLOW_PHOTO_CAPTURE)
    }
    if (!isPhotoCaptureOpen) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isPhotoCaptureOpen, photoCaptureWizardStep, dispatch, whoIsInThePhoto])


  let title
  switch (photoCaptureWizardStep) {
    case 0:
      title = `${botName} wants to take your photo`
      break;
    case 1:
      title = `${botName} wants to know who's in the photo`
      break;
  }
  const handleCapturePhoto = async () => {
    setIsDialogExpanded(true)
    await sleep(800)
    await capturePhoto()
    await sleep(800)
    setIsDialogExpanded(false)
  }

  return <BasicModal
    isOpen={!!isPhotoCaptureOpen}
    setIsOpen={setIsOpen}
    title={title}
    className={`${isDialogExpanded ? styles.expandedDialog : ''}`}
    content={!isDialogExpanded && <div className={`${styles.container}`}>
      {photoCaptureWizardStep === 0 && <>{capturedImage &&
        <img className={styles.capturedImage} src={capturedImage} alt="captured"/>}
        <CardActions sx={{justifyContent: "center"}}>
          <Button variant="contained" color="secondary" style={{fontSize: '42px'}} size="large"
                  onClick={handleCapturePhoto}>{capturedImage ? "Retake" : "Take a photo"}</Button>
          {capturedImage && <Button variant="contained" color="secondary" style={{fontSize: '42px', marginLeft: '20px'}}
                                    onClick={() => dispatch(setPhotoCaptureWizardStep(1))} size="large"
          >Approve</Button>}
        </CardActions></>
      }
      {photoCaptureWizardStep === 1 && <>   {/* note: can't put this part into a smaller component https://stackoverflow.com/questions/42573017/in-react-es6-why-does-the-input-field-lose-focus-after-typing-a-character   */}
        <TextField
          className={styles.whoIsInThePhoto}
          variant="outlined"
          placeholder="Your name here"
          value={whoIsInThePhoto}
          onKeyDown={() => resetAutoClose()}
          onChange={(e) => {
            resetAutoClose()
            dispatch(setWhoIsInThePhoto(e.target.value))
          }}
        />
        <CardActions sx={{justifyContent: "center"}}>
          <div className={styles.warning}>
            If you save the photo now, {botName} might show it to other people
          </div>
          <Button disabled={!whoIsInThePhoto} variant="contained" color="secondary" style={{fontSize: '42px'}} size="large"
                  onClick={() => dispatch(savePhoto(capturedImage, whoIsInThePhoto))}
          >Save Photo</Button>
        </CardActions>
      </>
      }
    </div>}
    footer={<div>
      <div className={styles.closeContainer}>
        <CardActions sx={{justifyContent: "center"}}>
          <Button onClick={() => setIsOpen(false)}
                  variant="contained"
                  color="primary"
                  type="button"
                  style={{fontSize: '22px'}}
                  startIcon={<CloseIcon/>}
          >{capturedImage ? "Close And Delete Photo" : "Close"}</Button>
        </CardActions>
      </div>
    </div>}
  />
}
